// Persists the (large, 6MB+) refList API response in IndexedDB so that on a
// page reload we can serve the previously fetched data to the UI immediately,
// instead of showing every dropdown in a loading state until the API responds.
// localStorage is intentionally avoided here since its quota (~5MB) is too
// small for this payload.

const DB_NAME = "jms-ref-list-cache";
const DB_VERSION = 1;
const STORE_NAME = "keyval";
const REF_LISTS_KEY = "refLists";

const openDatabase = () =>
  new Promise((resolve, reject) => {
    if (!window.indexedDB) {
      reject(new Error("IndexedDB is not supported in this browser"));
      return;
    }
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      request.result.createObjectStore(STORE_NAME);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const idbGet = async (key) => {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const request = tx.objectStore(STORE_NAME).get(key);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

const idbSet = async (key, value) => {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};

const idbDelete = async (key) => {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};

export const loadCachedRefLists = async () => {
  try {
    const cached = await idbGet(REF_LISTS_KEY);
    return cached ?? null;
  } catch (error) {
    console.error("Failed to read cached refLists from IndexedDB:", error);
    return null;
  }
};

export const saveCachedRefLists = async (data) => {
  if (!data) return;
  try {
    await idbSet(REF_LISTS_KEY, data);
  } catch (error) {
    console.error("Failed to persist refLists to IndexedDB:", error);
  }
};

// Call on logout so the next staff member to use this browser doesn't get
// served the previous user's cached reference-list data.
export const clearCachedRefLists = async () => {
  try {
    await idbDelete(REF_LISTS_KEY);
  } catch (error) {
    console.error("Failed to clear cached refLists from IndexedDB:", error);
  }
};
